import './styles/main.css';
import './styles/spa.css';
import './styles/app.css';
/**
 * The design system (claude.ai/design "Family Portal Design System"). Its own header says to
 * load it after main.css, but spa.css and app.css are later sheets carrying the v1 component
 * rules it replaces -- toasts are defined in all three -- and at equal specificity the last
 * sheet wins. So it goes last, and the tokens and components actually take effect.
 * Step 3 of its rollout is to delete the now-dead overrides from the sheets above.
 */
import './styles/shanks-ds.css';

import { auth } from './auth.js';
import { api } from './api.js';
import { router } from './router.js';
import { renderNavigation, hideNavigation } from './components/navigation.js';
import { renderPublicNav, hidePublicNav } from './components/public-nav.js';
import { showToast } from './utils.js';

import { renderLogin } from './pages/login.js';
import { renderForgotPassword } from './pages/forgot-password.js';
import { renderResetPassword } from './pages/reset-password.js';
import { renderHome } from './pages/home.js';
import { renderGallery } from './pages/gallery.js';
import { renderShared } from './pages/shared.js';
import { renderProfile } from './pages/profile.js';
import { renderAdmin } from './pages/admin.js';
import { renderCalendar } from './pages/calendar.js';
import { renderEvents } from './pages/events.js';
import { renderEventAdmin } from './pages/event-admin.js';
import { renderEventGuest } from './pages/event-guest.js';
import { renderPrivacy } from './pages/privacy.js';
import { renderDeleteAccount } from './pages/delete-account.js';
import { renderLanding } from './pages/landing.js';

/**
 * Routes only a signed-OUT visitor should see. A signed-in user is bounced away from
 * these, which is right for a login form and wrong for anything else.
 */
const GUEST_ONLY_ROUTES = new Set(['/login', '/forgot-password', '/reset-password']);

/**
 * Open to everyone, signed in or not. Google Play requires both to be reachable without an
 * account -- a store reviewer opens them cold -- and a signed-in user must see them too, so
 * they are neither guarded nor guest-only.
 */
const PUBLIC_ROUTES = new Set(['/', '/privacy', '/delete-account']);

/**
 * `/` is two pages: the family's home when signed in, the public landing page when not.
 * One route rather than a redirect, so a visitor who signs in stays where they were and
 * a shared link to the site works for family and strangers alike.
 */
const renderRoot = () => (auth.isAuthenticated ? renderHome() : renderLanding());

/**
 * The event guest page is open to everyone: a guest has no portal account at all, and an
 * organiser signed in on the same phone must still be able to open their own event page.
 * That is a different thing from GUEST_ONLY_ROUTES, and conflating the two is why this is
 * a second predicate rather than another entry in that set.
 */
const isEventGuestRoute = (path) => path.startsWith('/e/');

let loadingNoteTimer = null;

/**
 * The boot screen: a skeleton of the app's frame plus a line saying what is happening. It used
 * to be a bare spinner on a dimmed page, which says nothing during the ~30s the database can
 * take to wake after a quiet spell -- so after a few seconds this explains itself rather than
 * leaving someone wondering whether it has hung.
 */
function showLoading(visible) {
    document.getElementById('loading')?.classList.toggle('hidden', !visible);

    const note = document.getElementById('loading-note');
    clearTimeout(loadingNoteTimer);
    if (!note) return;
    note.textContent = '';
    if (!visible) return;
    loadingNoteTimer = setTimeout(() => {
        note.textContent = 'The database has been asleep — waking it can take up to half a minute.';
    }, 5000);
}

/**
 * Starts the database waking while the page is still loading, so the first real request does not
 * have to. Fire-and-forget: it is an optimisation, and a failure changes nothing.
 *
 * Not for robots. Waking the cluster costs money and keeps it up for minutes, which is not worth
 * paying for a crawler or a scanner that will never sign in. A crawler that only fetches HTML
 * never runs this at all; these checks cover automation that does execute scripts, and the API
 * refuses the obvious user agents as well.
 */
function warmDatabase() {
    if (navigator.webdriver) return;
    // A prerendered or background tab may never be looked at.
    if (document.prerendering || document.visibilityState === 'hidden') return;
    void api.warmup().catch(() => {});
}

router
    .route('/', renderRoot)
    .route('/home', renderHome)
    .route('/login', renderLogin)
    .route('/forgot-password', renderForgotPassword)
    .route('/reset-password', renderResetPassword)
    .route('/gallery', renderGallery)
    .route('/shared', renderShared)
    .route('/profile', renderProfile)
    .route('/admin', renderAdmin)
    .route('/calendar', renderCalendar)
    .route('/events', renderEvents)
    .route('/events/:id', renderEventAdmin)
    .route('/e/:slug', renderEventGuest)
    .route('/privacy', renderPrivacy)
    .route('/delete-account', renderDeleteAccount);

router.setNotFound(() => {
    document.getElementById('app').innerHTML = `
        <div class="container">
            <div class="empty-state">
                <i class="fa-solid fa-compass"></i>
                <h2>Page not found</h2>
                <p>That link doesn't go anywhere.</p>
                <a href="/" data-link class="btn btn-primary">Back to home</a>
            </div>
        </div>`;
});

/**
 * Auth guard. Returning a path from beforeNavigate redirects; returning true proceeds.
 * Admin is checked here rather than inside the page so an unauthorised user never sees
 * a flash of admin chrome -- the server enforces it regardless.
 */
router.setBeforeNavigate((path) => {
    // Open to everyone, signed in or not, and checked first so neither rule below applies.
    if (isEventGuestRoute(path)) return true;
    if (PUBLIC_ROUTES.has(path)) return true;

    const guestOnly = GUEST_ONLY_ROUTES.has(path);

    if (!auth.isAuthenticated && !guestOnly) return '/login';
    if (auth.isAuthenticated && guestOnly) return '/';
    if (path === '/shared' && auth.isLimited()) return '/';
    if (path === '/admin' && !auth.isAdmin()) {
        showToast('Admin access required', 'error');
        return '/';
    }
    return true;
});

// Drawn before the page renders as well as after, so the sidebar is never missing while
// a page loads and can't be lost to a page handler that throws.
// The event guest page deliberately has no sidebar: most of its visitors have no account
// to navigate with, and it doubles as the big-screen view at the venue. Suppressed in both
// hooks -- doing it in the page itself would work until setAfterNavigate redrew it.
router.setBeforeRender((path) => {
    const guestPage = isEventGuestRoute(path);
    // Toggled centrally rather than set by the page: there is no teardown hook, so a class
    // the page added itself would survive onto whatever the user opened next.
    document.body.classList.toggle('event-guest-page', guestPage);
    // The event background is an inline custom property on <body>. Clear it here for the
    // same reason the class is toggled here: there is no teardown hook, so a background set
    // by the guest page would otherwise follow the user onto every page after it.
    if (!guestPage) {
        document.body.classList.remove('has-event-bg');
        document.body.style.removeProperty('--event-bg');
    }
    if (guestPage) {
        hideNavigation();
        hidePublicNav();
        return;
    }
    // Signed out, the three-bar public header replaces the sidebar. Never both: the sidebar
    // is for someone with pages to navigate between, and a visitor has none.
    if (!auth.isAuthenticated) {
        hideNavigation();
        renderPublicNav();
        return;
    }
    hidePublicNav();
    renderNavigation();
});

router.setAfterNavigate((path) => {
    if (!isEventGuestRoute(path)) {
        if (auth.isAuthenticated) renderNavigation();
        else renderPublicNav();
    }
    window.scrollTo(0, 0);
});

// A page that fails to render should say so rather than leaving a half-drawn screen.
router.setOnError(() => {
    showToast('Something went wrong loading that page', 'error');
});

// A 401 that survives a refresh attempt means the session is unrecoverable; bounce to
// login from wherever the user happened to be rather than leaving a broken page up.
window.addEventListener('auth:logout', hideNavigation);

async function boot() {
    showLoading(true);
    // Clears the pre-cookie refresh token out of localStorage. See api.js for why.
    api.purgeLegacyRefreshToken();
    warmDatabase();
    try {
        await auth.init();
    } catch (err) {
        console.error('Session restore failed', err);
    } finally {
        showLoading(false);
    }

    // Preserve the deep link the user actually asked for; the guard redirects if needed.
    await router.navigate(window.location.pathname + window.location.search, { replace: true });
}

boot();
